import React from "react";
import { Text, View, Link, StyleSheet } from "@react-pdf/renderer";
import parse from "html-react-parser";

export class HtmlToReactPdf {
  constructor(templateStyles = {}) {
    this.templateStyles = templateStyles;
    // This object holds the "memory" of the parser across all pages.
    this.parsingState = {
      h1Rendered: false,
      contactInfoRendered: false,
    };
    console.log(
      "HtmlToReactPdf instance created with initial state:",
      this.parsingState
    );

    this.styles = StyleSheet.create({
      text: { fontSize: 11, lineHeight: 1.4 },
      paragraph: { marginBottom: 6, fontSize: 11, lineHeight: 1.4 },
      heading1: {
        fontSize: 24,
        fontWeight: "bold",
        marginTop: 16,
        marginBottom: 8,
      },
      heading3: {
        fontSize: 14,
        fontWeight: "bold",
        marginTop: 12,
        marginBottom: 6,
      },
      bold: { fontWeight: "bold" },
      italic: { fontStyle: "italic" },
      underline: { textDecoration: "underline" },
      link: { color: "#1971c2", textDecoration: "underline" },
      list: { marginLeft: 0, marginBottom: 8, marginTop: 4 },
      listItem: {
        flexDirection: "row",
        marginBottom: 6,
        alignItems: "flex-start",
        width: "100%",
      },
      bullet: { width: 15, fontSize: 10, lineHeight: 1.4 },
      listText: { flex: 1, fontSize: 11, lineHeight: 1.4, paddingLeft: 8 },
      jobHeader: { marginTop: 10, marginBottom: 5 },
      jobCompanyTitle: { fontSize: 11, fontWeight: "bold", marginBottom: 2 },
      jobDatesLocation: {
        fontSize: 10,
        fontStyle: "italic",
        color: "#6b7280",
        marginBottom: 5,
      },
      eduHeader: { marginTop: 10, marginBottom: 5 },
      eduUniDegree: { fontSize: 11, fontWeight: "bold", marginBottom: 2 },
      eduDatesLocation: {
        fontSize: 10,
        fontStyle: "italic",
        color: "#6b7280",
        marginBottom: 5,
      },
    });
  }

  parseInlineStyles(element, forceCenter = false) {
    const styles = {};
    if (!element.attribs?.style) return styles;

    element.attribs.style.split(";").forEach((rule) => {
      const [property, value] = rule.split(":").map((s) => s.trim());
      if (!property || !value) return;

      switch (property.toLowerCase()) {
        case "font-size":
          let size;
          if (value.includes("pt")) size = parseFloat(value);
          else if (value.includes("px")) size = parseFloat(value) * 0.75;
          else size = parseFloat(value);
          if (!isNaN(size)) styles.fontSize = size;
          break;
        case "text-align":
          // If forceCenter is true, ignore the text-align from the HTML
          if (!forceCenter) {
            styles.textAlign = value;
          }
          break;
        default:
          break;
      }
    });
    return styles;
  }

  convertElement(element, index) {
    if (element.type === "text") return element.data;
    if (element.type !== "tag") return null;

    const { name: tag, attribs, children: elementChildren } = element;
    const children = elementChildren
      ? elementChildren.map((child, i) => this.convertElement(child, i))
      : [];
    const className = attribs?.class || "";

    if (tag === "div") {
      const inlineStyles = this.parseInlineStyles(element);
      if (className.includes("job-header"))
        return (
          <View key={index} style={this.styles.jobHeader}>
            {children}
          </View>
        );
      if (className.includes("edu-header"))
        return (
          <View key={index} style={this.styles.eduHeader}>
            {children}
          </View>
        );
      if (className.includes("job-company-title"))
        return (
          <Text key={index} style={[this.styles.jobCompanyTitle, inlineStyles]}>
            {children}
          </Text>
        );
      if (className.includes("job-dates-location"))
        return (
          <Text
            key={index}
            style={[this.styles.jobDatesLocation, inlineStyles]}
          >
            {children}
          </Text>
        );
      if (className.includes("edu-uni-degree"))
        return (
          <Text key={index} style={[this.styles.eduUniDegree, inlineStyles]}>
            {children}
          </Text>
        );
      if (className.includes("edu-dates-location"))
        return (
          <Text
            key={index}
            style={[this.styles.eduDatesLocation, inlineStyles]}
          >
            {children}
          </Text>
        );
    }

    switch (tag) {
      case "p": {
        let isContactInfo = false;
        const isEmpty = !element.children.some(
          (child) =>
            child.type === "tag" ||
            (child.type === "text" && child.data.trim() !== "")
        );

        // Get text content for logging
        const textContent = element.children
          .filter((child) => child.type === "text")
          .map((child) => child.data)
          .join("")
          .trim()
          .substring(0, 50);

        console.log(`Processing <p> tag: "${textContent}..."`);
        console.log("Current parser state:", this.parsingState);
        console.log("Is empty paragraph:", isEmpty);

        // --- THIS IS THE KEY LOGIC ---
        // If the main H1 has been rendered, but we haven't centered the contact info yet,
        // AND this paragraph is not empty...
        if (
          this.parsingState.h1Rendered &&
          !this.parsingState.contactInfoRendered &&
          !isEmpty
        ) {
          // ...then this is the contact info.
          isContactInfo = true;
          // Set the flag so this only ever happens once for the entire document.
          this.parsingState.contactInfoRendered = true;
          console.log(">>> CONTACT INFO DETECTED! Centering this paragraph.");
        }
        // --- END OF KEY LOGIC ---

        // Parse inline styles, but force center alignment for contact info
        const inlineStyles = this.parseInlineStyles(element, isContactInfo);

        const paragraphStyles = isContactInfo
          ? [this.styles.paragraph, { textAlign: "center" }, inlineStyles]
          : [this.styles.paragraph, inlineStyles];

        if (isEmpty) return <View key={index} style={{ height: 16 }} />;
        return (
          <Text key={index} style={paragraphStyles}>
            {children}
          </Text>
        );
      }
      case "h1": {
        console.log("Processing <h1> tag");
        console.log("h1Rendered before:", this.parsingState.h1Rendered);

        // This is the trigger. We only do this for the VERY FIRST H1 in the document.
        if (!this.parsingState.h1Rendered) {
          this.parsingState.h1Rendered = true;
          console.log(">>> FIRST H1 DETECTED! Setting h1Rendered to true.");
        }

        // Parse inline styles but force center alignment for H1
        const inlineStyles = this.parseInlineStyles(element, true);

        const h1Styles = {
          ...(this.templateStyles.heading1 || this.styles.heading1),
          ...inlineStyles,
          textAlign: "center", // Force center alignment
        };
        return (
          <Text key={index} style={h1Styles}>
            {children}
          </Text>
        );
      }
      case "h3": {
        const inlineStyles = this.parseInlineStyles(element);
        const h3Styles = [
          this.templateStyles.heading3 || this.styles.heading3,
          inlineStyles,
        ];
        return (
          <Text key={index} style={h3Styles}>
            {children}
          </Text>
        );
      }
      case "span":
        const inlineStyles = this.parseInlineStyles(element);
        return (
          <Text key={index} style={inlineStyles}>
            {children}
          </Text>
        );
      case "strong":
      case "b":
        return (
          <Text key={index} style={this.styles.bold}>
            {children}
          </Text>
        );
      case "em":
      case "i":
        return (
          <Text key={index} style={this.styles.italic}>
            {children}
          </Text>
        );
      case "u":
        return (
          <Text key={index} style={this.styles.underline}>
            {children}
          </Text>
        );
      case "a":
        return (
          <Link key={index} src={attribs?.href || "#"} style={this.styles.link}>
            {children}
          </Link>
        );
      case "ul":
        return (
          <View key={index} style={this.styles.list}>
            {children.filter((c) => c !== null)}
          </View>
        );
      case "ol":
        const numberedChildren = React.Children.toArray(
          children.filter((c) => c !== null)
        ).map((child, i) =>
          React.cloneElement(child, {
            key: `li-${i}`,
            children: [
              <Text key={`bullet-${i}`} style={this.styles.bullet}>{`${
                i + 1
              }. `}</Text>,
              ...React.Children.toArray(child.props.children).slice(1),
            ],
          })
        );
        return (
          <View key={index} style={this.styles.list}>
            {numberedChildren}
          </View>
        );
      case "li":
        const liChildren =
          children.length === 1 && children[0].type === Text
            ? children[0].props.children
            : children;
        return (
          <View key={index} style={this.styles.listItem} wrap={false}>
            <Text style={this.styles.bullet}>• </Text>
            <Text
              style={[this.styles.listText, this.parseInlineStyles(element)]}
            >
              {liChildren}
            </Text>
          </View>
        );
      case "br":
        return "\n";
      case "div":
        return (
          <View key={index} style={this.parseInlineStyles(element)}>
            {children}
          </View>
        );
      default:
        return <>{children}</>;
    }
  }

  parse(html) {
    console.log("=== HtmlToReactPdf.parse() called ===");
    console.log("Parser state at start of parse:", this.parsingState);
    console.log(
      "HTML preview:",
      html ? html.substring(0, 150) + "..." : "null"
    );

    if (!html) return null;
    try {
      const cleanHtml = html.replace(/>\s+</g, "><").trim();
      const options = {
        replace: (domNode, index) => this.convertElement(domNode, index),
      };
      const parsed = parse(cleanHtml, options);
      const result = Array.isArray(parsed) ? parsed : [parsed];

      console.log("Parser state at end of parse:", this.parsingState);

      return result.filter((item) => item !== null && item !== undefined);
    } catch (error) {
      console.error("Error parsing HTML for PDF:", error);
      return [<Text key="error">Error rendering content</Text>];
    }
  }
}
